// src/utils/reportCalculations.js
/* ------------------------------------------------------------------
   Recalculates Varmetab and savings figures for the PDF report from the
   inputs that the calculators persist on the project.

   The calculators only persist their inputs (rows, indices, prices …);
   the results are computed while rendering. These helpers mirror:
   - VarmetabBody.jsx      (design / reference frame, glass, 50 % fradrag)
   - SavingsCalculator.jsx (kWh, kr and fuel units per saved scenario)
------------------------------------------------------------------- */

import constructionTypesJson from "../data/constructionTypes.json";
import linjetabTypesJson from "../data/linjetabTypes.json";
import energySrc from "../data/energySources.json";
import efficiencySrc from "../data/efficiencyFactors.json";
import { sumLoss } from "./varmetabCalculations";
import { costSaved, fuelSaved } from "./savingsCalculations";

/* ───────── Varmetab ───────────────────────────────────────── */

const PLAN_TYPES = constructionTypesJson.constructionTypes ?? constructionTypesJson;
const LINE_TYPES = linjetabTypesJson.linjetabTypes ?? linjetabTypesJson;
const REF_COL = { helårs: "Udefault", sommer: "UdefaultSummer" };

const getRefU = (item, bType = "helårs") =>
    item?.[REF_COL[bType]] ?? item?.Udefault ?? null;

const isGlassName = (name = "") =>
    /^(vinduer|ovenlysvinduer?|ovenlyskupler?|yderdøre?\s*med\s*glas)$/i.test(name);
const isWallName = (name = "") => /ydervæg$/i.test(name);
const isAutoRow = (r) => String(r?.id ?? "").startsWith("__extra");

const asRows = (v) => (Array.isArray(v) ? v.filter((r) => r && typeof r === "object") : []);

/* Same defaults as makeRow() → first type: tInd 20 / tUde −12 */
const withTemps = (r) => ({ Ti: 20, Te: -12, ...r });

export function computeVarmetabFrame(data = {}) {
    const info = data?.info ?? {};
    const bType = info.bType === "sommer" ? "sommer" : "helårs";
    const projectType = info.projectType ?? "tilbygning";
    const faNum = parseFloat(info.floorArea);

    const designPlan = asRows(data?.designPlan).filter((r) => !isAutoRow(r)).map(withTemps);
    const designLine = asRows(data?.designLine).map(withTemps);
    const coverPlan = asRows(data?.coverPlan);

    const hasData = designPlan.length > 0 || designLine.length > 0;
    const design = sumLoss(designPlan) + sumLoss(designLine);

    const base = {
        hasData,
        bType,
        projectType,
        floorArea: Number.isFinite(faNum) && faNum > 0 ? faNum : null,
        design,
    };

    if (projectType !== "tilbygning") return base;

    /* ---- glass KPIs ---- */
    const limitPerc = bType === "helårs" ? 0.22 : 0.3;
    const glassLimit = Number.isFinite(faNum) && faNum > 0 ? faNum * limitPerc : null;

    const glassArea = designPlan.reduce((sum, r) => {
        const name = PLAN_TYPES[r.typeKey]?.name ?? "";
        const a = Number(r?.AorL) || 0;
        return a > 0 && isGlassName(name) ? sum + a : sum;
    }, 0);

    const glassFrac =
        glassArea > 0 && glassLimit != null && faNum > 0 ? (100 * glassArea) / faNum : null;

    const extraGlass = glassLimit != null ? Math.max(0, glassArea - glassLimit) : null;

    /* ---- reference: plane elements ---- */
    const refBase = designPlan
        .filter((r) => r.partKind !== "line")
        .map((r) => {
            const src = PLAN_TYPES[r.typeKey] ?? {};
            return {
                partKind: "plan",
                UorPsi: getRefU(src, bType) ?? src.Udefault ?? r.UorPsi ?? 0,
                AorL: Number(r.AorL) || 0,
                Ti: r.Ti ?? 20,
                Te: r.Te ?? -12,
            };
        });

    let glassRows = [];
    if (extraGlass && extraGlass > 0) {
        const idxWin =
            designPlan.find((r) => r.typeKey != null && isGlassName(PLAN_TYPES[r.typeKey]?.name))
                ?.typeKey ?? PLAN_TYPES.findIndex((t) => isGlassName(t?.name));
        const idxWall =
            designPlan.find((r) => r.typeKey != null && isWallName(PLAN_TYPES[r.typeKey]?.name))
                ?.typeKey ?? PLAN_TYPES.findIndex((t) => isWallName(t?.name));

        const Uwin =
            idxWin != null && idxWin >= 0
                ? PLAN_TYPES[idxWin][REF_COL[bType]] ?? PLAN_TYPES[idxWin].Udefault ?? 1.2
                : 1.2;
        const Uwall =
            idxWall != null && idxWall >= 0
                ? PLAN_TYPES[idxWall][REF_COL[bType]] ?? PLAN_TYPES[idxWall].Udefault ?? 0.15
                : 0.15;

        glassRows = [
            { partKind: "plan", UorPsi: Uwin, AorL: -extraGlass, Ti: 20, Te: -12 },
            { partKind: "plan", UorPsi: Uwall, AorL: extraGlass, Ti: 20, Te: -12 },
        ];
    }

    /* ---- reference: fradrag (50 %) of covered plane elements ---- */
    const deductRows = coverPlan
        .filter((r) => r?.partKind === "plan" && Number(r.AorL) > 0 && Number(r.UorPsi) > 0)
        .map((r) => ({
            partKind: "plan",
            UorPsi: Number(r.UorPsi),
            AorL: 0.5 * Number(r.AorL),
            Ti: r.Ti ?? 20,
            Te: r.Te ?? -12,
        }));

    /* ---- reference: line losses ---- */
    const refLine = designLine.map((r) => {
        const src = LINE_TYPES[r.typeKey] ?? {};
        return {
            partKind: "line",
            UorPsi: getRefU(src, bType) ?? src.Udefault ?? r.UorPsi ?? 0,
            AorL: Number(r.AorL) || 0,
            Ti: r.Ti ?? src.tInd ?? 20,
            Te: r.Te ?? src.tUde ?? -12,
        };
    });

    const deduction = sumLoss(deductRows);
    const glassAdjust = sumLoss(glassRows);
    const reference = sumLoss(refBase) + glassAdjust + deduction + sumLoss(refLine);

    const diff = design - reference;
    const pct = Number.isFinite(reference) && reference !== 0 ? (diff / reference) * 100 : 0;

    return {
        ...base,
        reference,
        deduction,
        glassAdjust,
        diff,
        pct,
        ok: design <= reference,
        glassArea,
        glassLimit,
        glassFrac,
        extraGlass,
    };
}

/* ───────── Årlig besparelse ───────────────────────────────── */

const HEATING_OPTIONS = (efficiencySrc.efficiencyFactors ?? efficiencySrc).map((o, i) => ({
    ...o,
    id: o.id ?? String(i),
}));
const FUEL_OPTIONS = (energySrc.energySources ?? energySrc).map((o, i) => ({
    ...o,
    id: o.id ?? String(i),
}));

function pickByIdOrIndex(list, key) {
    if (key == null) return {};
    let hit = list.find((o) => o.id === key);
    if (hit) return hit;
    hit = list.find((o) => String(o.id) === String(key));
    if (hit) return hit;
    const idx = Number(key);
    return Number.isFinite(idx) ? list[idx] || {} : {};
}

const rowKWh = (r, HDD) => {
    const dU =
        Number.isFinite(r?.uBefore) && Number.isFinite(r?.uAfter)
            ? Math.max(0, r.uBefore - r.uAfter)
            : 0;
    return dU && r.area && HDD ? (dU * r.area * HDD * 24) / 1000 : 0;
};

export function computeSavingsScenario(s = {}) {
    const HEAT = pickByIdOrIndex(HEATING_OPTIONS, s?.heatIdx);
    const FUEL = pickByIdOrIndex(FUEL_OPTIONS, s?.fuelIdx);

    const efficiency = HEAT.efficiency ?? 1;
    const { energyContent = 1, unit = "", name: fuelName = "" } = FUEL;

    let kWhSaved;
    if (Number.isFinite(s?.kWhSaved)) {
        kWhSaved = s.kWhSaved; // legacy flat payload with stored result
    } else if (s?.mode === "single") {
        const one = s.single ?? {};
        kWhSaved = rowKWh(
            { uBefore: one.uBefore, uAfter: one.uAfter, area: one.areaOne },
            s.degreeDays ?? 3037
        );
    } else {
        const HDD = s?.buildingHDD ?? 3037;
        kWhSaved = asRows(s?.rows).reduce((acc, r) => acc + rowKWh(r, HDD), 0);
    }

    const annualCost = Number.isFinite(s?.annualCost)
        ? s.annualCost
        : costSaved({
            kWhSaved,
            efficiency,
            energyContent_perUnit: energyContent,
            pricePerUnit: s?.price ?? 0,
        });

    const annualUnits = Number.isFinite(s?.annualUnits)
        ? s.annualUnits
        : fuelSaved({ kWhSaved, efficiency, energyContent_perUnit: energyContent });

    return {
        name: s?.name || "Scenario",
        kWhSaved,
        annualCost,
        annualUnits,
        unit: s?.unit || unit,
        fuelName: s?.fuelName || fuelName,
        heatingName: HEAT.description || "",
    };
}
